import { usePendingSuggestions } from "@/hooks/use-pending-suggestions";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { useHotkeys } from "react-hotkeys-hook";
import { Reader } from "./Reader";
import { useMemo } from "react";

export const ReaderContainer = () => {
  const dispatch = useAppDispatch();
  const selectedBlockIndex = useAppState((state) => state.selectedBlockIndex);
  const currentDocument = useAppState((state) => state.currentDocument);
  const referencePanelVisible = useAppState(
    (state) => state.referencePanel.visible
  );
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const selectedSuggestionIndex = useAppState(
    (state) => state.selectedSuggestionIndex
  );
  const focusMode = useAppState((state) => state.focusMode);
  const pendingSuggestions = usePendingSuggestions();

  const visibleBlocks = useMemo(() => {
    if (!currentDocument) return [];
    if (!selectedGroup) return currentDocument.blocks;
    return currentDocument.blocks.filter((block) =>
      selectedGroup.blockIds.includes(block.id)
    );
  }, [currentDocument, selectedGroup]);

  useHotkeys(
    "up",
    () => {
      if (selectedSuggestionIndex !== null) {
        // We're in suggestions, navigate up within suggestions
        if (selectedSuggestionIndex > 0) {
          dispatch({
            type: "SET_SELECTED_SUGGESTION_INDEX",
            payload: selectedSuggestionIndex - 1,
          });
        } else {
          // At first suggestion, clear selection and select last block
          if (selectedGroup && selectedGroup.blockIds.length > 0) {
            dispatch({
              type: "CLEAR_SUGGESTION_SELECT_BLOCK",
              payload: selectedGroup.blockIds.length - 1,
            });
          } else {
            dispatch({
              type: "SET_SELECTED_SUGGESTION_INDEX",
              payload: null,
            });
          }
        }
      } else {
        // We're in blocks, navigate up
        let prevIndex = selectedBlockIndex - 1;
        while (prevIndex >= 0 && visibleBlocks[prevIndex]?.type === 'agent') {
          prevIndex--;
        }

        if (prevIndex >= 0) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: {
              index: prevIndex,
              fromKeyboard: true,
            },
          });
        }
      }
    },
    { keydown: true },
    [selectedBlockIndex, selectedSuggestionIndex, selectedGroup, visibleBlocks]
  );

  useHotkeys(
    "down",
    () => {
      if (currentDocument && selectedGroup) {
        if (selectedSuggestionIndex !== null) {
          // We're in suggestions, navigate down within suggestions
          if (selectedSuggestionIndex < pendingSuggestions.length - 1) {
            dispatch({
              type: "SET_SELECTED_SUGGESTION_INDEX",
              payload: selectedSuggestionIndex + 1,
            });
          }
        } else {
          // We're in blocks
          let nextIndex = selectedBlockIndex + 1;
          while (nextIndex < visibleBlocks.length && visibleBlocks[nextIndex]?.type === 'agent') {
            nextIndex++;
          }

          if (nextIndex < visibleBlocks.length) {
            dispatch({
              type: "SET_SELECTED_BLOCK_INDEX",
              payload: { index: nextIndex, fromKeyboard: true },
            });
          } else if (pendingSuggestions.length > 0) {
            // At last block, move to first suggestion
            dispatch({
              type: "SET_SELECTED_SUGGESTION_INDEX",
              payload: 0,
            });
          } else {
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: "smooth",
            });
          }
        }
      }
    },
    { keydown: true },
    [
      currentDocument?.blocks,
      selectedGroup,
      selectedBlockIndex,
      selectedSuggestionIndex,
      visibleBlocks,
    ]
  );

  useHotkeys(
    "mod+up",
    () => {
      if (currentDocument && selectedGroup) {
        if (selectedBlockIndex > 0) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: 0,
          });
        }
      }
    },
    { keydown: true },
    [currentDocument?.blocks, selectedGroup, selectedBlockIndex]
  );

  useHotkeys(
    "mod+down",
    () => {
      if (currentDocument && selectedGroup) {
        if (selectedBlockIndex < selectedGroup.blockIds.length - 1) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: selectedGroup.blockIds.length - 1,
          });
        }
      }
    },
    { keydown: true },
    [currentDocument?.blocks, selectedGroup, selectedBlockIndex]
  );

  // Add hotkeys for group navigation
  useHotkeys(
    "mod+left",
    () => {
      if (currentDocument && selectedGroup) {
        const currentIndex = currentDocument.groups.findIndex(
          (group) => group.id === selectedGroup.id
        );
        if (currentIndex > 0) {
          dispatch({
            type: "SET_SELECTED_GROUP",
            payload: currentDocument.groups[currentIndex - 1],
          });
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [currentDocument, selectedGroup]
  );

  useHotkeys(
    "mod+right",
    () => {
      if (currentDocument && selectedGroup) {
        const currentIndex = currentDocument.groups.findIndex(
          (group) => group.id === selectedGroup.id
        );
        if (currentIndex < currentDocument.groups.length - 1) {
          dispatch({
            type: "SET_SELECTED_GROUP",
            payload: currentDocument.groups[currentIndex + 1],
          });
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [currentDocument, selectedGroup]
  );

  useHotkeys(
    "shift+mod+f",
    (e) => {
      e.preventDefault();
      dispatch({ type: "ENTER_FOCUS_MODE" });
    },
    { enableOnFormTags: true }
  );

  useHotkeys(
    "esc",
    () => {
      if (focusMode) dispatch({ type: "EXIT_FOCUS_MODE" });
    },
    [focusMode]
  );

  useHotkeys(
    "mod+slash",
    () => {
      dispatch({ type: "TOGGLE_KEYBOARD_SHORTCUTS" });
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
    }
  );

  useHotkeys(
    "bracketleft",
    () => {
      dispatch({ type: "TOGGLE_COPILOT" });
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "bracketright",
    () => {
      dispatch({
        type: "SHOW_REFERENCE_PANEL",
        payload: {
          visible: !referencePanelVisible,
          view: "references",
        },
      });
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "mod+k",
    (e) => {
      e.preventDefault();
      dispatch({ type: "TOGGLE_COMMAND_MENU" });
    },
    {
      enableOnFormTags: true,
    }
  );

  // Context panel navigation shortcuts
  useHotkeys(
    "1",
    (e) => {
      // Only fire if Alt is not pressed (to avoid conflict with alt+1)
      if (!e.altKey) {
        e.preventDefault();
        dispatch({
          type: "SHOW_REFERENCE_PANEL",
          payload: {
            visible: true,
            view: "toc",
          },
        });
      }
    },
    {
      enableOnFormTags: false,
    }
  );

  useHotkeys(
    "2",
    (e) => {
      // Only fire if Alt is not pressed (to avoid conflict with alt+2)
      if (!e.altKey) {
        e.preventDefault();
        dispatch({
          type: "SHOW_REFERENCE_PANEL",
          payload: {
            visible: true,
            view: "references",
          },
        });
      }
    },
    {
      enableOnFormTags: false,
    }
  );

  useHotkeys(
    "3",
    (e) => {
      // Only fire if Alt is not pressed (to avoid conflict with alt+3)
      if (!e.altKey) {
        e.preventDefault();
        dispatch({
          type: "SHOW_REFERENCE_PANEL",
          payload: {
            visible: true,
            view: "notes",
          },
        });
      }
    },
    {
      enableOnFormTags: false,
    }
  );

  const toggleKeyboardShortcuts = () =>
    dispatch({ type: "TOGGLE_KEYBOARD_SHORTCUTS" });
  const toggleCommandMenu = () => dispatch({ type: "TOGGLE_COMMAND_MENU" });
  const toggleCopilot = () => dispatch({ type: "TOGGLE_COPILOT" });
  const toggleDiscrepanciesPanel = () =>
    dispatch({ type: "TOGGLE_DISCREPANCIES_PANEL" });

  return (
    <Reader
      toggleKeyboardShortcuts={toggleKeyboardShortcuts}
      toggleCommandMenu={toggleCommandMenu}
      toggleCopilot={toggleCopilot}
      toggleDiscrepanciesPanel={toggleDiscrepanciesPanel}
    />
  );
};
